import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { Link, useNavigate } from "react-router-dom"
import classNames from "classnames"
import { withTranslation } from "react-i18next"
import withRouter from "components/Common/withRouter"
import PropTypes from "prop-types"
import OrdersTable from "pages/vendor/Orders/OrdersTable"
import { FilterData, IndexType } from "components/Extra"
import { OrdersGrid } from "pages/vendor/Orders/OrdersGrid"
import { AssignDeliveryManModal, RejectedOrderModal } from "pages/vendor/Orders/Modals"
import { CurrentTapType, PaymentStatus } from "./ordersType"
import { useTranslation } from "react-i18next"
import { Card, CardBody, Col, Row } from "reactstrap"
import { useSearchParams } from "react-router-dom"
import { FilterByDateRangeModal } from "components/Extra/FilterByDateRangeModal"
import { getOrders, updateOrderStatus } from "../../../store/orders/actions"

const tabs = [
  CurrentTapType.all,
  CurrentTapType.pending,
  CurrentTapType.inProgress,
  CurrentTapType.delivered,
  CurrentTapType.cancelled,
  CurrentTapType.rejected,
]

export interface Order {
  id: number
  issueTime: string
  statusName: string
  statusId: number
  subTotal: number
  discount: number
  deliveryCost: number
  total: number
  isPaid: boolean
  paymentMethod: string | null
  transactionReference: string | null
  customerName: string
  customerId: number
  terminalName: string | null
  terminalId: number | null
}

interface OrdersProps {
  location?: any
  isDashboardPage?: boolean
}

export const Orders: React.FC<OrdersProps> = ({
  location,
  isDashboardPage,
}) => {
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentTap, setCurrentTap] = useState(CurrentTapType.all)
  const [activeTab, setActiveTab] = useState<IndexType>("list-ul")

  // Get orders from Redux store
  const { orders, loading, pagination } = useSelector((state: any) => state.Orders)

  // Store all orders for card counts (fetched without status filter)
  const [allOrders, setAllOrders] = useState<Order[]>([])

  const [filteredOrders, setFilteredOrders] = useState<Order[]>([])
  const [checkedOrders, setCheckedOrders] = useState<Order[]>([])
  const [openRejectedOrderModal, setOpenRejectedOrderModal] = useState<{
    open: boolean
    order: Order | null
  }>({
    open: false,
    order: null,
  })
  const [openAssignDeliveryManModal, setOpenAssignDeliveryManModal] = useState<{
    open: boolean
    order: Order | null
  }>({
    open: false,
    order: null,
  })

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0)
  const pageSize = 10

  const navigate = useNavigate()
  const { t } = useTranslation()

  // Fetch orders on mount and page change
  useEffect(() => {
    const statusFilter = currentTap === CurrentTapType.all
      ? undefined
      : getStatusIdFromTap(currentTap)

    dispatch(getOrders({
      PageSize: pageSize,
      PageIndex: currentPage,
      StatusId: statusFilter,
    }, { push: navigate }))
  }, [dispatch, currentPage, currentTap])

  // Capture all orders when on "All" tab for card counts
  useEffect(() => {
    if (currentTap === CurrentTapType.all) {
      setAllOrders(orders as Order[])
    }
  }, [orders, currentTap])

  // Helper function to convert tap to status ID
  const getStatusIdFromTap = (tap: CurrentTapType): number | undefined => {
    switch (tap) {
      case CurrentTapType.pending:
        return 1
      case CurrentTapType.inProgress:
        return 3
      case CurrentTapType.delivered:
        return 5
      case CurrentTapType.cancelled:
        return 6
      case CurrentTapType.rejected:
        return 7
      default:
        return undefined
    }
  }

  // Filter orders based on current tap
  useEffect(() => {
    if (currentTap === CurrentTapType.all) {
      setFilteredOrders(orders)
    } else {
      const statusId = getStatusIdFromTap(currentTap)
      setFilteredOrders(orders.filter((order: Order) => order.statusId === statusId))
    }
  }, [orders, currentTap])

  const checkedOrderHandler = useCallback((order: Order) => {
    setCheckedOrders(prev =>
      prev.includes(order) ? prev.filter(o => o !== order) : [...prev, order]
    )
  }, [])

  const changeTabHandler = useCallback(
    (tab: CurrentTapType) => {
      setCurrentTap(tab)
      setCurrentPage(0)
    },
    [setCurrentTap]
  )

  useEffect(() => {
    const tabParam = searchParams.get("tab") as CurrentTapType
    if (tabParam && Object.values(CurrentTapType).includes(tabParam)) {
      changeTabHandler(tabParam)
    }
  }, [changeTabHandler, searchParams])

  const acceptHandler = useCallback((order: Order) => {
    // Accept order - update status to In Progress (statusId: 3)
    const statusData = {
      StatusId: 3,
      ActionId: 3,
      Notes: "Resolved",
    }
    dispatch(updateOrderStatus(order.id, statusData, { push: navigate }))
  }, [dispatch, navigate])
  const rejectHandler = useCallback((order: Order) => {
    setOpenRejectedOrderModal({
      open: true,
      order: order,
    })
  }, [])
  const viewDetailsHandler = useCallback((order: Order) => {
    navigate(`/orders/${order.id}`)
  }, [navigate])

  const isAllChecked = useMemo(() => {
    return filteredOrders.length > 0 && checkedOrders.length === filteredOrders.length
  }, [filteredOrders, checkedOrders])

  const toggleAllOrders = useCallback(() => {
    setCheckedOrders(isAllChecked ? [] : filteredOrders)
  }, [isAllChecked, filteredOrders])

  const onDateRangeChange = (startDate: string, endDate: string) => {
    console.log("Selected date range:", startDate, "to", endDate)
    // TODO: Implement date range filtering
  }

  // Calculate status counts from all orders (not just filtered)
  // Use existing card labels, update values with real counts from backend
  const ordersCardData: {label: string; value: number}[] = useMemo(() => {
    // Use allOrders captured when on "All" tab, fallback to current orders
    const ordersForCounts = allOrders.length > 0 ? allOrders : (orders as Order[])

    return [
      { label: "orders_totalOrders", value: pagination.totalCount || 0 },
      { label: "orders_pending", value: ordersForCounts.filter(o => o.statusId === 1).length },
      { label: "orders_inProgress", value: ordersForCounts.filter(o => o.statusId === 3).length },
      { label: "orders_cancelled", value: ordersForCounts.filter(o => o.statusId === 6).length },
      { label: "orders_delivered", value: ordersForCounts.filter(o => o.statusId === 5).length },
      { label: "orders_rejected", value: ordersForCounts.filter(o => o.statusId === 7).length },
    ]
  }, [allOrders, orders, pagination.totalCount])

  if (isDashboardPage) {
    return (
      <>
        <div className="">
          <FilterData
            data={orders}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            bearerFilteredData={setFilteredOrders}
            showFilterBtnInsteadOfToggle
          />
        </div>

        {/* Modals */}
        <RejectedOrderModal
          isOpen={openRejectedOrderModal.open}
          order={openRejectedOrderModal.order}
          toggle={() =>
            setOpenRejectedOrderModal({
              open: false,
              order: null,
            })
          }
        />
        <AssignDeliveryManModal
          isOpen={openAssignDeliveryManModal.open}
          order={openAssignDeliveryManModal.order}
          toggle={() =>
            setOpenAssignDeliveryManModal({
              open: false,
              order: null,
            })
          }
        />

        <OrdersTable
          orders={filteredOrders}
          isAllChecked={isAllChecked}
          checkedOrders={checkedOrders}
          toggleAllOrders={toggleAllOrders}
          checkedOrderHandler={checkedOrderHandler}
          acceptHandler={acceptHandler}
          rejectHandler={rejectHandler}
          viewDetailsHandler={viewDetailsHandler}
        />
      </>
    )
  }

  return (
    <div className="page-content d-flex flex-column">
      <div className="container-fluid p-0">
        <Breadcrumbs
          title={t("dashboard_dashboard")}
          breadcrumbItem={t("orders_orders")}
        />
        <FilterByDateRangeModal
          onDateRangeChange={onDateRangeChange}
          className="mb-3"
        />
        <Row>
          {ordersCardData.map((one, key) => (
            <Col
              md="4"
              lg="2.4"
              key={"_col_" + key}
              className="col-lg-6ths"
              style={{ height: "fit-content" }}
            >
              <Card className="mini-stats-wid shadow-md rounded-3">
                <CardBody>
                  <div className="d-flex">
                    <div className="flex-grow-1">
                      <p className="text-muted fw-medium text-capitalize">
                        {t(one.label)}
                      </p>
                      <div className="d-flex gap-1 align-items-baseline justify-content-start">
                        <h4 className="mb-0">{one.value}</h4>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <ul className="nav nav-pills mb-3 px-2 ">
          {tabs.map(tab => (
            <li
              className="nav-item"
              style={{ flex: 1, textAlign: "center" }}
              key={tab}
            >
              <Link
                to="#"
                className={classNames(
                  { active: currentTap === tab },
                  "nav-link"
                )}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => changeTabHandler(tab)}
              >
                {t(tab)}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <FilterData
        data={orders}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        bearerFilteredData={setFilteredOrders}
        showSearchFilterBtn
      />

      {/* Modals */}
      <RejectedOrderModal
        isOpen={openRejectedOrderModal.open}
        order={openRejectedOrderModal.order}
        toggle={() =>
          setOpenRejectedOrderModal({
            open: false,
            order: null,
          })
        }
      />
      <AssignDeliveryManModal
        isOpen={openAssignDeliveryManModal.open}
        order={openAssignDeliveryManModal.order}
        toggle={() =>
          setOpenAssignDeliveryManModal({
            open: false,
            order: null,
          })
        }
      />

      {activeTab == "list-ul" ? (
        <OrdersTable
          orders={filteredOrders}
          isAllChecked={isAllChecked}
          checkedOrders={checkedOrders}
          toggleAllOrders={toggleAllOrders}
          checkedOrderHandler={checkedOrderHandler}
          acceptHandler={acceptHandler}
          rejectHandler={rejectHandler}
          viewDetailsHandler={viewDetailsHandler}
        />
      ) : (
        <OrdersGrid
          orders={filteredOrders}
          acceptHandler={acceptHandler}
          rejectHandler={rejectHandler}
          viewDetailsHandler={viewDetailsHandler}
        />
      )}
    </div>
  )
}

Orders.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(Orders))
